import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { LoadingType } from "../../../types/CommonTypes";
import { addCommissionAmount, getSaleDetailsById, getSaleDetailsByInvoice, updateCommissionDate } from "../../../service/SaleDetailsService";
import { useAppSelector } from "../redux-hooks";

export interface AddCommissionRequest {
  agencyId: number;
  commissionAgencyType: string;
  invoiceId?: number;
  commissionAmount: number;
  commissionReceivedAmount: number;
  v3CommissionReceivedAmount?: number;
  payoutDate: string;
  payoutComment?: string;
  paymentId?: number;
}

export interface UpdateCommissionDateRequest {
  agencyId: number;
  agencyType: string;
  invoiceId: number;
  commissionDate: string;
  paymentId: number;
}

export interface SalesDetailsDTO {
  id?: number;
  mfg: string;
  mfgConfirmNum: string;
  invoiceNum: string;
  invoiceAdded?: boolean;
  invoiceId?: string;
  trackingNum: string;
  v3OrderId: string;
  orderAdded?: boolean;
  orderId?: string;
  serialNumber: string;
  orderDate: string;
  orderType: string;
  orderMonthYear: string;
  accountPractice: string;
  product: string;
  prodSize: string;
  allProdSizes?: string;
  totalUnitsSqcm: number;
  prodQty: number;
  cogsPu: number;
  cogsSalesOrig: number;
  cogsSalesPmt: string;
  provider: string;
  dos: string;
  pt: number;
  acctInvoiceStatus: string;
  totalCommPercent: number;
  totalComm: number;
  payStatusToV3: string;
  v3NetComm: number;
  agency: string;
  accountRep: string;
  agencyPercent: number;
  agencyComm: number;
  override: string;
  overridePercent: number;
  overrideComm: number;
  agencyPayStatus: string;
  ovrAePayStatus: string;
  notes?: string;
  sort?: string;
  completeStatus?: boolean;
}

type SalesDetailsState = {
  SalesDetailsInfo?: SalesDetailsDTO[] | [];
  status: LoadingType;
}

const initialState: SalesDetailsState = {
  SalesDetailsInfo: undefined,
  status: "idle"
};

export const getSalesDetailsByOrderId = createAsyncThunk("getSalesDetailsByOrderId", async (id: number) => {
  const response = await getSaleDetailsById(id);
  // const v3Response = response.data;
  const v3Response = response?.data; // CS-1984
  return v3Response;
});

export const getSalesDetailsByInvoiceId = createAsyncThunk("getSalesDetailsByInvoiceId", async (id: number) => {
  const response = await getSaleDetailsByInvoice(id);
  // const v3Response = response.data;
  const v3Response = response?.data; // CS-1984
  return v3Response;
});

export const addCommission = createAsyncThunk("addCommission", async (data: AddCommissionRequest & { skipLoader?: boolean }) => {
  const skipLoader = !!data?.skipLoader;
  const payload = skipLoader
    ? (({ skipLoader: _skip, ...rest }) => rest)(data)
    : data;
  const response = skipLoader
    ? await addCommissionAmount(payload as AddCommissionRequest, true)
    : await addCommissionAmount(data);
  const v3Response = response.data;
  return v3Response;
});

export const updateCommission = createAsyncThunk("updateCommission", async (data: UpdateCommissionDateRequest) => {
  const response = await updateCommissionDate(data);
  const v3Response = response.data;
  return v3Response;
});

const SaleDetailsSlice = createSlice({
  name: "SalesDetails",
  initialState,
  reducers: {
    resetSaleDetailsStatus: (state: SalesDetailsState) => {
      state.SalesDetailsInfo = []
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getSalesDetailsByOrderId.pending, (state, action) => {
        state.status = "loading"
      })
      .addCase(getSalesDetailsByOrderId.fulfilled, (state, action) => {
        state.status = "success";
        // state.SalesDetailsInfo = action.payload.data;
        state.SalesDetailsInfo = action.payload?.data; // CS-1984
      })
      .addCase(getSalesDetailsByOrderId.rejected, (state, action) => {
        state.status = "error"
      })
      .addCase(getSalesDetailsByInvoiceId.pending, (state, action) => {
        state.status = "loading"
      })
      .addCase(getSalesDetailsByInvoiceId.fulfilled, (state, action) => {
        state.status = "success";
        // state.SalesDetailsInfo = action.payload.data;
        state.SalesDetailsInfo = action.payload?.data; // CS-1984
      })
      .addCase(getSalesDetailsByInvoiceId.rejected, (state, action) => {
        state.status = "error"
      });
  },
});

export const { resetSaleDetailsStatus } = SaleDetailsSlice.actions;
export const useSaleDetailsSlice = () => useAppSelector((state) => state.salesDetailsSlice);
export default SaleDetailsSlice.reducer;